import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Typography,
  type PopoverProps,
} from "@mui/material";
import { blueGrey, green, red } from "@mui/material/colors";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  PatientContext,
  PatientDispatchContext,
} from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import { useFetch } from "../../services/hooks/useFetch.ts";
import { projectsPrefix } from "../../services/ProjectsServices.ts";
import SnippetsEnabledTextField from "../SnippetsEnabledTextField.tsx";

interface MarkModalProps {
  open: PopoverProps["open"];
  onClose: () => void;
  anchorEl: PopoverProps["anchorEl"];
}

export default function MarkModal({ open, onClose, anchorEl }: MarkModalProps) {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const patient = useContext(PatientContext)!;
  const dispatch = useContext(PatientDispatchContext);
  const { updatePatientSummaries } = useContext(ProjectContext)!;
  const [isIncluded, setIsIncluded] = useState<boolean | "" | "Undetermined">(
    patient.isIncluded ?? "",
  );
  const [reason, setReason] = useState(patient.reason ?? "");
  const projectId = searchParams.get("projectId");
  const {
    update: savePatientIncluded,
    data: patientData,
    isLoading: isSaving,
  } = useFetch<Patient, PatientIncludedRequest>();

  const handleClose = useCallback(() => {
    setIsIncluded("");
    setReason("");
    onClose();
  }, [setIsIncluded, onClose]);

  function onSubmit(event: { preventDefault: () => void }) {
    event.preventDefault();
    setLoading(true);
    const newData = {
      reason: reason,
      isIncluded:
        isIncluded === "Undetermined" || isIncluded === "" ? null : isIncluded,
    };

    if (newData.reason.trim() !== "") {
      void savePatientIncluded({
        path: `${projectsPrefix}/${projectId}/patients/${patient.id}/included`,
        body: newData,
      });
    }
  }

  useEffect(() => {
    if (!isSaving && patientData?.id) {
      setLoading(false);
      dispatch({ type: "update", patient: patientData });
      if (updatePatientSummaries) {
        updatePatientSummaries();
      }
      handleClose();
    }
  }, [
    isSaving,
    patientData,
    setLoading,
    handleClose,
    dispatch,
    updatePatientSummaries,
  ]);

  const { snippetsEnabledTextField } = SnippetsEnabledTextField({
    labelText: "Enter Note/Reason",
    textChanged: setReason,
    snippetType: "PATIENT",
    attributeTypeId: null,
    snippetDialogWidthPx: 580,
    snippetDialogHeightPx: 320,
  });

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <div className="categorize-modal">
        <div className="categorize-modal-title">
          <Typography variant="h6">Mark Patient As</Typography>
          <IconButton
            onClick={handleClose}
            sx={{ "&:hover": { bgcolor: "action.hover" } }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="categorize-dropdown">
          <FormControl size="small" className="mark-dropdown">
            <InputLabel>Mark Patient As</InputLabel>
            <Select
              label="Mark Patient As"
              value={String(isIncluded)}
              onChange={(event) => {
                const value = event.target.value;
                setIsIncluded(
                  value === "true"
                    ? true
                    : value === "false"
                      ? false
                      : "Undetermined",
                );
              }}
              variant="standard"
            >
              <MenuItem value={"true"}>
                <Box className="patient-modal-select-icon">
                  <ListItemIcon>
                    <ThumbUpOutlinedIcon
                      fontSize="small"
                      sx={{ color: green[600] }}
                    />
                  </ListItemIcon>
                  <ListItemText>Included</ListItemText>
                </Box>
              </MenuItem>
              <MenuItem value={"false"}>
                <Box className="patient-modal-select-icon">
                  <ListItemIcon>
                    <BlockOutlinedIcon
                      fontSize="small"
                      sx={{ color: red[600] }}
                    />
                  </ListItemIcon>
                  <ListItemText>Excluded</ListItemText>
                </Box>
              </MenuItem>
              <MenuItem value={"Undetermined"}>
                <Box className="patient-modal-select-icon">
                  <ListItemIcon>
                    <PersonOutlinedIcon
                      fontSize="small"
                      sx={{ color: blueGrey[600] }}
                    />
                  </ListItemIcon>
                  <ListItemText>Undetermined</ListItemText>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="categorize-modal-textfield">
          {snippetsEnabledTextField}
        </div>
        <div className="categorize-modal-footer-mark">
          <Button onClick={handleClose} variant={"outlined"} size={"small"}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            size={"small"}
            endIcon={loading && <CircularProgress size="1.5rem" />}
            disabled={reason.trim() === "" || isIncluded === "" || loading}
          >
            Submit
          </Button>
        </div>
      </div>
    </Popover>
  );
}
